import { jobPercent, type UploadJob } from '../../contexts/UploadContext';

export interface TransferSummary {
  total: number;
  completed: number;
  failed: number;
  inflight: number;
  /** Null when nothing in flight can report progress (downloads still preparing). */
  percent: number | null;
  allDone: boolean;
  allTakeoff: boolean;
  allSitePhotos: boolean;
  allDownloads: boolean;
  hasDownloads: boolean;
  mixed: boolean;
  preparing: boolean;
}

export function summariseTransfers(jobs: UploadJob[]): TransferSummary {
  const total = jobs.length;
  const completed = jobs.filter((j) => j.status === 'success').length;
  const failed = jobs.filter((j) => j.status === 'error').length;
  const live = jobs.filter((j) => j.status === 'uploading' || j.status === 'pending');
  const inflight = live.length;
  const allTakeoff = total > 0 && jobs.every((j) => j.kind === 'takeoff');
  const allSitePhotos = total > 0 && jobs.every((j) => j.kind === 'site-photo');
  const allDownloads = total > 0 && jobs.every((j) => j.kind === 'download');
  const hasDownloads = jobs.some((j) => j.kind === 'download');
  const mixed = hasDownloads && !allDownloads;

  let percent: number | null;
  if (hasDownloads) {
    // Downloads only count once the server reports a figure; otherwise the
    // bar would sit at 0% for a ZIP that is nearly ready.
    const measured = jobs.filter((j) => j.kind !== 'download' || j.percent !== undefined);
    percent = measured.length > 0
      ? Math.round(measured.reduce((sum, j) => sum + jobPercent(j), 0) / measured.length)
      : null;
    if (allDownloads && !jobs.some((j) => j.percent !== undefined)) percent = null;
  } else if (jobs.some((j) => j.kind === 'takeoff' || j.kind === 'site-photo')) {
    percent = Math.round(jobs.reduce((sum, j) => sum + jobPercent(j), 0) / total);
  } else {
    const totalBytes = jobs.reduce((sum, j) => sum + j.fileSize, 0);
    const sentBytes = jobs.reduce((sum, j) => sum + Math.min(j.bytesSent, j.fileSize), 0);
    percent = totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0;
  }

  const preparing = live.some((j) => j.kind === 'download' && j.stage !== 'Downloading');
  return {
    total, completed, failed, inflight, percent,
    allDone: inflight === 0 && total > 0,
    allTakeoff, allSitePhotos, allDownloads, hasDownloads, mixed, preparing,
  };
}

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function transferHeaderLabel(s: TransferSummary): string {
  if (s.mixed) {
    if (!s.allDone) return count(s.inflight, 'transfer');
    if (s.failed === 0) return `${count(s.completed, 'transfer')} complete`;
    if (s.completed === 0) return `${count(s.failed, 'transfer')} failed`;
    return `${s.completed} complete · ${s.failed} failed`;
  }
  if (s.allDownloads) {
    if (!s.allDone) return `${s.preparing ? 'Preparing' : 'Downloading'} ${count(s.inflight, 'download')}`;
    if (s.failed === 0) return `${count(s.completed, 'download')} ready`;
    if (s.completed === 0) return `${count(s.failed, 'download')} failed`;
    return `${s.completed} ready · ${s.failed} failed`;
  }
  const noun = s.allTakeoff ? 'drawing file' : s.allSitePhotos ? 'site photo' : 'file';
  const plural = (n: number) => `${noun}${n === 1 ? '' : 's'}`;
  if (s.allDone) {
    if (s.failed === 0) return `${s.allTakeoff ? 'Imported' : 'Uploaded'} ${s.completed} ${plural(s.completed)}`;
    if (s.completed === 0) return `${s.failed} ${s.allTakeoff ? 'import' : 'upload'}${s.failed === 1 ? '' : 's'} failed`;
    return `${s.completed} ${s.allTakeoff ? 'imported' : 'uploaded'} · ${s.failed} failed`;
  }
  if (s.allSitePhotos) {
    return `Uploading site photos · ${s.completed + s.failed} of ${s.total} finished`;
  }
  return `${s.allTakeoff ? 'Importing' : 'Uploading'} ${s.inflight} of ${s.total} · ${s.percent}%`;
}
